import { Link, useNavigate } from 'react-router-dom';
import {
	GovCard,
	GovDropdown,
	GovFlex,
	GovGrid,
	GovGridItem,
	GovIcon,
	GovLayout,
	GovLayoutColumn,
} from '@gov-design-system-ce/react';
import { useLanguageNamespaceLoader, useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useRozcestnikContext } from '@gov-nx/module/page';
import { Breadcrumbs, Button, LayoutGap, Tabs } from '@gov-nx/ui/web';
import { truncate } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { FavoriteToggle } from '../../Favorite/FavoriteToggle';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { Skeleton } from '../../Skeleton/Skeleton';

export interface ServicesViewProps {
	code: PageCode;
}

export const ServicesView = ({ code }: ServicesViewProps) => {
	const {
		data,
		numberOfSignposts,
		loadMore,
		canLoadMore,
		filter,
		setFilterFavourites,
		isSignpostDataFetching,
		setFilterAll,
		isSortedAlphabetically,
		onToggleSorting,
	} = useRozcestnikContext();

	const { t } = useLocale(code);
	const translations = useLanguageNamespaceLoader(code);
	const navigate = useNavigate();

	if (translations.isLoading) return null;

	return (
		<LayoutGap gap="l">
			<Breadcrumbs
				options={[
					{ label: t('domu'), toPage: 'dashboard' },
					{ label: t('sluzby') },
				]}
			/>
			<h1>{t('sluzby')}</h1>
			<GovLayout
				type="text"
				variant="left">
				<GovLayoutColumn>
					<Tabs
						items={[
							{
								label: t('tab.vsechny-kategorie'),
								triggerIdentifier: 'all',
								isActive: !filter.favourites,
								// eslint-disable-next-line react/jsx-no-useless-fragment
								children: <></>,
							},
							{
								iconStart: (
									<GovIcon
										type="templates"
										name="star"
									/>
								),
								label: t('tab.oblibene'),
								triggerIdentifier: 'favourites',
								isActive: filter.favourites,
								// eslint-disable-next-line react/jsx-no-useless-fragment
								children: <></>,
							},
						]}
						onChange={(identifier) => {
							if (identifier === 'favourites') {
								navigate(pageRouteResolver('sluzby.oblibene'));
								setFilterFavourites();
							}

							if (identifier === 'all') {
								navigate(pageRouteResolver('sluzby'));
								setFilterAll();
							}
						}}
					/>
					<LayoutGap
						gap="xl"
						className="items-center">
						<GovFlex
							justifyContent="space-between"
							alignItems="center"
							className="w-full">
							{isSignpostDataFetching ? (
								<Skeleton
									height="24px"
									width="80px"
								/>
							) : (
								<p>{t('sluzby-pocet', { count: numberOfSignposts })}</p>
							)}

							<GovDropdown
								position="right"
								color="primary"
								size="m"
								type="base"
								aria-label={
									t('akce.razeni') +
									': ' +
									(isSortedAlphabetically
										? t('akce.abecedne')
										: t('akce.nejcasteji-navstevovane'))
								}
								label={
									<>
										<GovIcon
											name="sort-down"
											type="templates"
											slot="icon-start"
										/>
										<span className="gov-text-weight--normal">
											{t('akce.razeni')}:{' '}
										</span>
										{isSortedAlphabetically
											? t('akce.abecedne')
											: t('akce.nejcasteji-navstevovane')}
										<GovIcon
											name="chevron-down"
											type="components"
											slot="icon-end"
										/>
									</>
								}>
								<Button
									color="neutral"
									size="m"
									type="base"
									isExpanded
									onClick={onToggleSorting}>
									{isSortedAlphabetically
										? t('akce.nejcasteji-navstevovane')
										: t('akce.abecedne')}
								</Button>
							</GovDropdown>
						</GovFlex>

						{isSignpostDataFetching ? (
							<LayoutGap
								gap="l"
								className="w-full">
								<Skeleton
									width="100%"
									height="128px"
								/>
								<Skeleton
									width="100%"
									height="128px"
								/>
							</LayoutGap>
						) : (
							<GovGrid gap="l">
								{data.map((signpost) => {
									const renderContent = (
										<LayoutGap gap="s">
											<h3 className="text-headline-s! text-(--text-primary-color) pr-12">
												{signpost.name}
											</h3>
											{signpost.description ? (
												<div className="text-(--text-secondary)">
													<MarkdownRender>
														{truncate(signpost.description, 150)}
													</MarkdownRender>
												</div>
											) : null}
										</LayoutGap>
									);

									return (
										<GovGridItem
											column="12"
											key={signpost.id}
											className="relative">
											<GovCard asChild>
												{signpost.action.isLink ? (
													<Link
														to={signpost.action.to}
														aria-label={
															t('wcag.zobrazit-sluzbu') + signpost.name
														}>
														{renderContent}
													</Link>
												) : (
													<button
														aria-label={
															t('wcag.zobrazit-sluzbu') + signpost.name
														}
														onClick={signpost.action.onClick}
														className="text-left">
														{renderContent}
													</button>
												)}
											</GovCard>
											{signpost.code ? (
												<FavoriteToggle
													code={signpost.code}
													name={signpost.name}
													size="m"
													customClass="absolute top-[10px] right-(--spacing-m)"
												/>
											) : null}
										</GovGridItem>
									);
								})}
							</GovGrid>
						)}

						{canLoadMore ? (
							<Button
								type="outlined"
								isLoading={isSignpostDataFetching}
								color="primary"
								onClick={() => loadMore(10)}>
								{t('akce.nacist-dalsich-10')}
							</Button>
						) : null}
					</LayoutGap>
				</GovLayoutColumn>
			</GovLayout>
		</LayoutGap>
	);
};
